import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import "../styles.css";

const HERO_IMAGE = "/gallery/1885680.avif";

function NotFoundComponent() {
  return (
    <div id="main-content" className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div id="main-content" className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#b4232c" },
      { name: "google-site-verification", content: "HsGxqB-MRIu1JZsrbX8oVBzo0QWD7smmN6l3K9LZfHY" },
      { title: "Winnipeg CPR & First Aid Training | Red Cross Certified" },
      {
        name: "description",
        content:
          "Red Cross certified CPR, AED and First Aid training in Winnipeg, MB. 30+ years of experience. Group, workplace and private courses. Book your class today.",
      },
      { name: "author", content: "Safety First Winnipeg" },
      { property: "og:title", content: "Winnipeg CPR & First Aid Training | Red Cross Certified" },
      {
        property: "og:description",
        content:
          "Red Cross certified CPR, AED and First Aid training in Winnipeg with 30+ years of expertise. Book your class today.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Safety First Winnipeg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Winnipeg CPR & First Aid Training | Red Cross Certified" },
      {
        name: "twitter:description",
        content:
          "Red Cross certified CPR, AED and First Aid training in Winnipeg with 30+ years of expertise. Book your class today.",
      },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/H11f8sPnVxbVMPidooAs9ItoKYe2/social-images/social-1779141223661-0_0.webp",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/H11f8sPnVxbVMPidooAs9ItoKYe2/social-images/social-1779141223661-0_0.webp",
      },
    ],
    links: [{ rel: "icon", href: "/favicon.ico" }],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en-CA">
      <head>
        <link
          rel="preload"
          as="image"
          href={HERO_IMAGE}
          type="image/avif"
          fetchPriority="high"
          media="(min-width: 768px)"
        />
        <HeadContent />
        <noscript>
          <link rel="stylesheet" href="/fonts/faces.css" />
        </noscript>
      </head>
      <body>
        <a href="#main-content" className="skip-link">
          Skip to content
        </a>
        {children}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){function loadFonts(){var l=document.createElement("link");l.rel="stylesheet";l.href="/fonts/faces.css";document.head.appendChild(l);}if(window.requestAnimationFrame)requestAnimationFrame(function(){requestAnimationFrame(loadFonts);});else setTimeout(loadFonts,0);window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}function loadGtag(){if(window.__gtagLoaded)return;window.__gtagLoaded=1;var s=document.createElement("script");s.src="https://www.googletagmanager.com/gtag/js?id=G-NVGGNZK3H2";s.async=true;s.onload=function(){gtag("js",new Date());gtag("config","G-NVGGNZK3H2");};document.head.appendChild(s);}["pointerdown","keydown","touchstart"].forEach(function(e){window.addEventListener(e,loadGtag,{once:true,passive:true});});})();`,
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return <Outlet />;
}
